// apps/web/src/components/dashboard/TopIntelligenceFeed.tsx
//
// KAN-209 Briefing Desk — "Top intelligence": ranked editorial story
// rows with quiet dividers. Ranking is the existing maxRiskScore (no
// invented velocity metric). Rows are <li> inside an <ol>, NOT
// <article>, so the §3.1.1 tile lint rule does not constrain them; each
// row still keeps to exactly one link (the stretched title) to avoid
// nested-interactive traps.

import { useMemo, useState, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { formatDistanceToNow } from 'date-fns'
import { Skeleton } from '@/components/ui/skeleton'
import { SeverityBadge } from '@/components/shared/SeverityBadge'
import { TierBadge } from '@/components/stories/TierBadge'
import { ConfidenceBadge } from '@/components/stories/ConfidenceBadge'
import { cn } from '@/lib/utils'
import type { Severity, StoryCardShape } from '@/types/story'

const FEED_LIMIT = 12

type TypeBucket = 'all' | 'threat' | 'vuln' | 'supply' | 'reg' | 'other'

const TYPE_FILTERS: Array<{ id: TypeBucket; label: string }> = [
  { id: 'all', label: 'All signals' },
  { id: 'threat', label: 'Threat landscape' },
  { id: 'vuln', label: 'Vulnerabilities' },
  { id: 'supply', label: 'Supply chain' },
  { id: 'reg', label: 'Regulatory' },
  { id: 'other', label: 'Other' },
]

const SECTION_BUCKETS: Record<string, TypeBucket> = {
  threat_landscape_ir: 'threat',
  vulnerabilities_exploitation: 'vuln',
  supply_chain_third_party: 'supply',
  cloud_saas_identity: 'supply',
  governance_risk_compliance: 'reg',
}

const SEVERITY_FILTERS: Severity[] = ['critical', 'high', 'medium']

function bucketOf(story: StoryCardShape): TypeBucket {
  return SECTION_BUCKETS[story.section] ?? 'other'
}

function FilterPill(props: {
  label: string
  pressed: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      aria-pressed={props.pressed}
      onClick={props.onClick}
      className={cn(
        'rounded-full border px-3 py-1 text-xs font-medium transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        props.pressed
          ? 'border-primary bg-secondary text-foreground'
          : 'border-border text-muted-foreground hover:bg-secondary hover:text-foreground',
      )}
    >
      {props.label}
    </button>
  )
}

function FeedRow(props: { story: StoryCardShape; rank: number }) {
  const s = props.story
  return (
    <li className="group relative grid grid-cols-[2rem_minmax(0,1fr)] gap-3 border-b border-border py-3.5 transition-colors last:border-0 hover:bg-secondary/50 focus-within:bg-secondary/50 sm:grid-cols-[2rem_minmax(0,1fr)_auto]">
      <span className="pt-0.5 font-mono text-sm text-muted-foreground">
        {String(props.rank).padStart(2, '0')}
      </span>
      <div className="min-w-0">
        <h3 className="font-heading text-[15px] font-semibold leading-snug">
          <Link
            to={`/stories/${s.id}`}
            className="after:absolute after:inset-0 focus-visible:outline-none group-hover:text-primary"
          >
            {s.title}
          </Link>
        </h3>
        <p className="mt-1 line-clamp-2 text-[13px] leading-5 text-muted-foreground">
          {s.excerpt}
        </p>
        <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
          <span className="font-medium text-foreground">
            {s.publisherBadge.name}
          </span>
          <TierBadge tier={s.publisherBadge.tier} />
          <span>
            {s.sourceCount} sources
            {s.independentOrgs > 0
              ? ` · ${s.independentOrgs.toLocaleString('en-GB')} orgs`
              : ''}
          </span>
          <ConfidenceBadge label={s.confidenceLabel} />
        </div>
      </div>
      <div className="col-start-2 flex items-center gap-2 sm:col-start-3 sm:flex-col sm:items-end">
        {s.maxSeverity ? <SeverityBadge severity={s.maxSeverity} /> : null}
        <time
          dateTime={s.latestSourceAddedAt}
          className="font-mono text-[11px] text-muted-foreground"
        >
          {formatDistanceToNow(new Date(s.latestSourceAddedAt), {
            addSuffix: true,
          })}
        </time>
      </div>
    </li>
  )
}

function FeedSkeleton() {
  return (
    <div role="status" aria-label="Loading intelligence feed">
      {Array.from({ length: 6 }).map((_, i) => (
        <div
          key={i}
          className="grid grid-cols-[2rem_minmax(0,1fr)_auto] gap-3 border-b border-border py-3.5 last:border-0"
        >
          <Skeleton className="h-4 w-6" />
          <div>
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="mt-1.5 h-3 w-full" />
            <Skeleton className="mt-2 h-3 w-1/2" />
          </div>
          <Skeleton className="h-5 w-16 rounded-full" />
        </div>
      ))}
    </div>
  )
}

export interface TopIntelligenceFeedProps {
  stories: StoryCardShape[] | undefined
  isLoading?: boolean
  isError?: boolean
  onRetry?: () => void
}

export function TopIntelligenceFeed(props: TopIntelligenceFeedProps) {
  const [type, setType] = useState<TypeBucket>('all')
  const [severities, setSeverities] = useState<Severity[]>([])
  const filtersActive = type !== 'all' || severities.length > 0

  const ranked = useMemo(
    () =>
      [...(props.stories ?? [])].sort((a, b) => b.maxRiskScore - a.maxRiskScore),
    [props.stories],
  )

  const visible = useMemo(
    () =>
      ranked
        .filter((s) => (type === 'all' ? true : bucketOf(s) === type))
        .filter((s) =>
          severities.length === 0
            ? true
            : s.maxSeverity !== null && severities.includes(s.maxSeverity),
        )
        .slice(0, FEED_LIMIT),
    [ranked, type, severities],
  )

  const clearFilters = () => {
    setType('all')
    setSeverities([])
  }

  const toggleSeverity = (sev: Severity) =>
    setSeverities((prev) =>
      prev.includes(sev) ? prev.filter((s) => s !== sev) : [...prev, sev],
    )

  let body: ReactNode
  if (props.isLoading) {
    body = <FeedSkeleton />
  } else if (props.isError) {
    body = (
      <div
        role="alert"
        className="rounded-lg border border-border bg-card px-4 py-8 text-center text-sm text-muted-foreground"
      >
        <p>Unable to load the intelligence feed.</p>
        <button
          type="button"
          onClick={props.onRetry}
          className="mt-3 rounded-md border border-border px-4 py-1.5 text-sm font-medium text-foreground transition-colors hover:border-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          Retry
        </button>
      </div>
    )
  } else if (ranked.length === 0) {
    body = (
      <p className="px-2 py-8 text-center text-sm text-muted-foreground">
        No intelligence in the current window.
      </p>
    )
  } else if (visible.length === 0) {
    body = (
      <div className="px-2 py-8 text-center text-sm text-muted-foreground">
        <p>No stories match the current filters.</p>
        <button
          type="button"
          onClick={clearFilters}
          className="mt-2 font-medium text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          Clear filters
        </button>
      </div>
    )
  } else {
    body = (
      <ol className="list-none">
        {visible.map((story, i) => (
          <FeedRow key={story.id} story={story} rank={i + 1} />
        ))}
      </ol>
    )
  }

  return (
    <div>
      <div
        role="group"
        aria-label="Filter intelligence"
        className="flex flex-wrap items-center gap-2"
      >
        {TYPE_FILTERS.map((f) => (
          <FilterPill
            key={f.id}
            label={f.label}
            pressed={type === f.id}
            onClick={() => setType(f.id)}
          />
        ))}
        <span aria-hidden="true" className="mx-1 h-4 w-px bg-border" />
        {SEVERITY_FILTERS.map((sev) => (
          <FilterPill
            key={sev}
            label={sev.charAt(0).toUpperCase() + sev.slice(1)}
            pressed={severities.includes(sev)}
            onClick={() => toggleSeverity(sev)}
          />
        ))}
        {filtersActive ? (
          <button
            type="button"
            onClick={clearFilters}
            className="text-xs font-medium text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            Clear all
          </button>
        ) : null}
      </div>
      <div className="mt-2">{body}</div>
    </div>
  )
}
